import { z } from 'zod'
import type { Minor } from './money'
import {
  BASIS_POINTS_FULL,
  addMinor,
  applyBasisPoints,
  assertMinor,
  maxMinor,
  minMinor,
  subMinor,
} from './money'

export const TAX_RATES_SETTING_KEY = 'tax.rates'

export const TAX_ESTIMATE_DISCLAIMER =
  'An estimate for orientation, not tax advice. It assumes the whole company is extracted in a single year at the rates in force on the date shown.'

export class TaxConfigError extends Error {}

export interface ExtractionTaxInput {
  date: string
  baseCurrency: string
  companyKey: string
  /** Value the subject ultimately holds through this company, in base currency. */
  companyValueMinor: Minor
  /** Cost basis behind that value, where positions carry one. */
  companyCostBasisMinor: Minor
  /**
   * The part of the unrealised gain the corporate charge reaches. It is not
   * value minus basis: a company's gains on unlisted shares are exempt under
   * aktieavancebeskatningsloven and a bank balance is not a security, so only
   * the holdings themselves can say what is chargeable.
   */
  taxableGainMinor: Minor
  settings: Readonly<Record<string, unknown>>
}

export interface ExtractionTax {
  taxMinor: Minor
  /**
   * No rates in force means nothing was subtracted, which is not the same
   * answer as a bill of zero and must never be shown as one.
   */
  ratesConfigured: boolean
}

/** Corporate gains plus dividend tax on extracting a holding company's value. */
export type ExtractionTaxFn = (input: ExtractionTaxInput) => ExtractionTax

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

const rateBp = z.number().int().min(0).max(BASIS_POINTS_FULL)

export const taxRateVersionSchema = z.object({
  effectiveFrom: z.string().regex(ISO_DATE, 'must be YYYY-MM-DD'),
  corporateGainsRateBp: rateBp,
  dividendLowRateBp: rateBp,
  dividendHighRateBp: rateBp,
  dividendThresholdMinor: z.number().int().nonnegative(),
})

export type TaxRateVersion = z.infer<typeof taxRateVersionSchema>

export const taxRateHistorySchema = z
  .array(taxRateVersionSchema)
  .refine(
    (versions) => new Set(versions.map((v) => v.effectiveFrom)).size === versions.length,
    'two versions share an effectiveFrom, so the rates on that date are ambiguous',
  )

export function parseTaxRateHistory(value: unknown): TaxRateVersion[] {
  const parsed = taxRateHistorySchema.safeParse(value)
  if (!parsed.success) {
    throw new TaxConfigError(
      parsed.error.issues
        .map((issue) => `${issue.path.join('.') || '(root)'}: ${issue.message}`)
        .join('; '),
    )
  }
  return sortByEffectiveFrom(parsed.data)
}

/**
 * Unconfigured and misconfigured both yield no rates, and no rates means no
 * deduction. Guessing a rate would put a number nobody chose into `spendable`.
 */
export function taxRateHistoryFrom(
  settings: Readonly<Record<string, unknown>>,
): TaxRateVersion[] {
  const parsed = taxRateHistorySchema.safeParse(settings[TAX_RATES_SETTING_KEY])
  return parsed.success ? sortByEffectiveFrom(parsed.data) : []
}

/**
 * The latest version in force on or before `date`, so adding a rate never
 * reprices the history behind it.
 */
export function ratesOn(
  history: readonly TaxRateVersion[],
  date: string,
): TaxRateVersion | undefined {
  let found: TaxRateVersion | undefined
  for (const version of sortByEffectiveFrom(history)) {
    if (version.effectiveFrom > date) break
    found = version
  }
  return found
}

export function upsertTaxRateVersion(
  history: readonly TaxRateVersion[],
  version: TaxRateVersion,
): TaxRateVersion[] {
  return sortByEffectiveFrom([
    ...history.filter((v) => v.effectiveFrom !== version.effectiveFrom),
    version,
  ])
}

export interface ExtractionEstimate {
  date: string
  rates: TaxRateVersion | null
  companyValueMinor: Minor
  companyCostBasisMinor: Minor
  unrealisedGainMinor: Minor
  deferredCorporateTaxMinor: Minor
  /** What is left in the company once the deferred bill is met. */
  distributableMinor: Minor
  dividendLowBaseMinor: Minor
  dividendHighBaseMinor: Minor
  dividendTaxMinor: Minor
  totalTaxMinor: Minor
  netMinor: Minor
}

export interface ExtractionInput {
  date: string
  companyValueMinor: Minor
  companyCostBasisMinor: Minor
  /** Omitted only where the caller holds two totals and no holdings to ask. */
  taxableGainMinor?: Minor | undefined
  rates?: TaxRateVersion | undefined
}

export function estimateExtraction(input: ExtractionInput): ExtractionEstimate {
  const companyValueMinor = assertMinor(input.companyValueMinor)
  const companyCostBasisMinor = assertMinor(input.companyCostBasisMinor)
  const rates = input.rates

  if (!rates) {
    return {
      date: input.date,
      rates: null,
      companyValueMinor,
      companyCostBasisMinor,
      unrealisedGainMinor: 0,
      deferredCorporateTaxMinor: 0,
      distributableMinor: companyValueMinor,
      dividendLowBaseMinor: 0,
      dividendHighBaseMinor: 0,
      dividendTaxMinor: 0,
      totalTaxMinor: 0,
      netMinor: companyValueMinor,
    }
  }

  // Denmark marks a company's *listed* portfolio shares to market each year, so
  // the liability exists before anything is sold — but only on the holdings the
  // charge reaches, which is why the gain is handed in rather than derived here.
  // A loss is not a credit: it would show up as money the company does not have.
  const chargeableMinor =
    input.taxableGainMinor === undefined
      ? subMinor(companyValueMinor, companyCostBasisMinor)
      : assertMinor(input.taxableGainMinor)
  const unrealisedGainMinor = maxMinor(chargeableMinor, 0)
  const deferredCorporateTaxMinor = applyBasisPoints(
    unrealisedGainMinor,
    rates.corporateGainsRateBp,
  )

  const distributableMinor = subMinor(companyValueMinor, deferredCorporateTaxMinor)
  const dividendBaseMinor = maxMinor(distributableMinor, 0)

  // The threshold is an annual personal allowance; charging everything above it
  // at the high rate is what extracting the company in one year costs. Paying it
  // out across several years would cost less, and this figure does not promise
  // that.
  const dividendLowBaseMinor = minMinor(dividendBaseMinor, rates.dividendThresholdMinor)
  const dividendHighBaseMinor = subMinor(dividendBaseMinor, dividendLowBaseMinor)
  const dividendTaxMinor = addMinor(
    applyBasisPoints(dividendLowBaseMinor, rates.dividendLowRateBp),
    applyBasisPoints(dividendHighBaseMinor, rates.dividendHighRateBp),
  )

  const totalTaxMinor = addMinor(deferredCorporateTaxMinor, dividendTaxMinor)

  return {
    date: input.date,
    rates,
    companyValueMinor,
    companyCostBasisMinor,
    unrealisedGainMinor,
    deferredCorporateTaxMinor,
    distributableMinor,
    dividendLowBaseMinor,
    dividendHighBaseMinor,
    dividendTaxMinor,
    totalTaxMinor,
    netMinor: subMinor(companyValueMinor, totalTaxMinor),
  }
}

export function extractionEstimateFor(input: ExtractionTaxInput): ExtractionEstimate {
  return estimateExtraction({
    date: input.date,
    companyValueMinor: input.companyValueMinor,
    companyCostBasisMinor: input.companyCostBasisMinor,
    taxableGainMinor: input.taxableGainMinor,
    rates: ratesOn(taxRateHistoryFrom(input.settings), input.date),
  })
}

export const settingsExtractionTax: ExtractionTaxFn = (input) => {
  const estimate = extractionEstimateFor(input)
  return { taxMinor: estimate.totalTaxMinor, ratesConfigured: estimate.rates !== null }
}

function sortByEffectiveFrom(versions: readonly TaxRateVersion[]): TaxRateVersion[] {
  return [...versions].sort((a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom))
}
